import React from 'react';
import moment from 'moment';
import useHomeContext from '@/hooks/useHomeContext';
import { DATE_FORMAT } from '@/components/DateRangeInput';
import Modal from '@/components/Modal';
import { logAnalyticsEvent } from '@/services/firebase';
import useAuth from '@/hooks/useAuth';
import {
  extractBankTransactionFilter,
  extractTransactionFilter
} from '@/utils/transactionHistoryFilter';
import {
  exportAccountTransaction,
  exportBusinessTransactions
} from '@/services/transactionHistory';
import { AccountTypeEnum } from '@/context/types/Account';
import { TExportTransactionResponse } from '@/services/types/TransactionData';
import { ResponseBody } from '@/services/types/ResponseBody';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose }) => {
  const { business, accountId, accountType } = useAuth()!;
  const {
    dateFilter,
    selectedAccount,
    accountsFilter,
    wallets,
    teamMemberFilter,
    transactionFilter,
    exportInProgress,
    setExportInProgress
  } = useHomeContext()!;

  const handleExport = async () => {
    try {
      setExportInProgress(true);

      logAnalyticsEvent('select_content', {
        content_type: 'button',
        content_id: 'export_transactions'
      });
      onClose();

      const filters = {
        startDate: dateFilter?.startDate
          ? new Date(dateFilter?.startDate).toISOString()
          : undefined,
        endDate: dateFilter?.endDate ? new Date(dateFilter?.endDate).toISOString() : undefined,
        walletIds: accountsFilter.length ? accountsFilter : undefined,
        customerIds: teamMemberFilter.length ? teamMemberFilter : undefined,
        transactionTypes: transactionFilter.length
          ? extractTransactionFilter(transactionFilter)
          : undefined,
        bankTransactionTypes: transactionFilter.length
          ? extractBankTransactionFilter(transactionFilter)
          : undefined,
        search: undefined
      };

      let response: ResponseBody<TExportTransactionResponse>;
      switch (accountType) {
        case AccountTypeEnum.PERSONAL: {
          response = await exportAccountTransaction(accountId!, filters);
          break;
        }

        case AccountTypeEnum.BUSINESS: {
          response = await exportBusinessTransactions(business!.id, filters);
        }
      }

      const link = document.createElement('a');
      link.href = response!.data.fileUrl;
      link.setAttribute('download', response!.data.fileName);
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error(err);
    } finally {
      setExportInProgress(false);
    }
  };

  const getExportAccountValue = () => {
    if (wallets.length > 1 && wallets.length === accountsFilter.length) {
      return 'All accounts';
    }

    if (accountsFilter.length > 0) {
      const getAccountName = (walletId: string | number) =>
        wallets.find((el) => el.id === walletId)?.name;
      return accountsFilter
        .map((id) => getAccountName(id))
        .filter(Boolean)
        .join(', ');
    }

    return selectedAccount.label;
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        headerText="Export Settings"
        headerVariant="xxl-medium">
        <div className="p-4 md:p-5 flex flex-col" style={{ gap: 36 }}>
          <div className="flex flex-col" style={{ gap: 8 }}>
            <span
              style={{
                fontSize: 14,
                fontFamily: 'General Sans',
                lineHeight: '20px',
                color: '#010B3B'
              }}>
              <span style={{ fontWeight: 500 }}>Accounts:</span>{' '}
              <span style={{ fontWeight: 400 }}>{getExportAccountValue()}</span>
            </span>
            <span
              style={{
                fontSize: 14,
                fontFamily: 'General Sans',
                lineHeight: '20px',
                color: '#010B3B'
              }}>
              <span style={{ fontWeight: 500 }}>Date Range:</span>{' '}
              <span style={{ fontWeight: 400 }}>
                {moment(dateFilter?.startDate).format(DATE_FORMAT)} {' - '}{' '}
                {moment(dateFilter?.endDate).format(DATE_FORMAT)}
              </span>
            </span>
            <span
              style={{
                fontSize: 14,
                fontFamily: 'General Sans',
                lineHeight: '20px',
                color: '#010B3B'
              }}>
              <span style={{ fontWeight: 500 }}>Filters:</span>{' '}
              <span style={{ fontWeight: 400 }}>Custom selections applied</span>
            </span>
            <span
              style={{
                fontSize: 14,
                fontFamily: 'General Sans',
                lineHeight: '20px',
                color: '#010B3B'
              }}>
              <span style={{ fontWeight: 500 }}>Export format:</span>{' '}
              <span style={{ fontWeight: 400 }}>CSV</span>
            </span>
            <span
              style={{
                fontSize: 12,
                fontFamily: 'General Sans',
                fontWeight: 500,
                color: '#4D5788'
              }}>
              Attached receipts will be in a .zip file
            </span>
          </div>
          <div className="flex justify-center items-end" style={{ gap: 20 }}>
            <button
              onClick={handleExport}
              disabled={exportInProgress}
              style={{
                minWidth: 120,
                paddingLeft: 36,
                paddingRight: 36,
                paddingTop: 16,
                paddingBottom: 16,
                background: '#11E8C2',
                borderRadius: 30,
                color: '#010F55',
                fontFamily: 'General Sans',
                fontWeight: 500,
                fontSize: 12,
                opacity: exportInProgress ? 0.5 : 1,
                cursor: exportInProgress ? 'not-allowed' : 'pointer',
                border: 'none',
                display: 'inline-flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
              Export
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ExportModal;
